import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Crown, Users, Briefcase, Shield, Database, Activity, Settings, LogOut, FileText, TrendingUp, Upload, Copy, BarChart3, Mail, Filter, SquareCheck as CheckSquare, Clock, MessageSquare, CheckCircle} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"
import SuperadminProfileSwitcher from "../components/SuperadminProfileSwitcher"

export default function SuperadminDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') || "overview"
  })

  useEffect(() => {
    // Update activeTab when URL params change
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    if (tab) {
      setActiveTab(tab)
    }
  }, [window.location.search])

  useEffect(() => {
    if (activeTab === "messages") {
      navigate("/dashboard/superadmin/messages")
    } else if (activeTab === "users") {
      navigate("/dashboard/superadmin/users")
    } else if (activeTab === "invites") {
      navigate("/dashboard/superadmin/invites")
    } else if (activeTab === "email_analytics") {
      navigate("/dashboard/superadmin/email-analytics")
    }
  }, [activeTab, navigate])

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const menuItems = [
    { id: "overview", label: "Overview", icon: Crown },
    { id: "users", label: "Users", icon: Users },
    { id: "applications", label: "Access Requests", icon: Briefcase },
    { id: "invites", label: "Invites", icon: Shield },
    { id: "messages", label: "Messaging", icon: MessageSquare },
    { id: "email_analytics", label: "Email Analytics", icon: Mail },
    { id: "audit", label: "Audit Logs", icon: FileText },
    { id: "settings", label: "Settings", icon: Settings }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white border-b border-amber-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Superadmin Dashboard</h1>
              <p className="text-sm text-amber-100">Full system access</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <SuperadminProfileSwitcher />
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-white hover:text-amber-200 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-2">
              {menuItems.map(item => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? "bg-amber-50 text-amber-700 font-semibold"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {activeTab === "overview" && <OverviewTab navigate={navigate} />}
              {activeTab === "applications" && <ApplicationsTab />}
              {activeTab === "audit" && <AuditLogsTab />}
              {activeTab === "settings" && <SettingsTab />}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

// Overview Tab
function OverviewTab({ navigate }: { navigate: any }) {
  const [kpis, setKpis] = React.useState({
    total_nurses: 0,
    nurses_24h: 0,
    nclex_passed_total: 0,
    nclex_passed_30d: 0
  })
  const [quickStats, setQuickStats] = React.useState({
    pending_applications: 0,
    active_invites: 0,
    expiring_invites_7d: 0
  })
  const [newestUsers, setNewestUsers] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadingUsers, setLoadingUsers] = React.useState(true)
  const [loadingQuickStats, setLoadingQuickStats] = React.useState(true)

  const fetchKPIs = async () => {
    try {
      const result = await lumi.entities.UserProfiles.list({
        filter: { role: "nurse" },
        limit: 10000
      })

      const nurses = result.list
      const now = new Date()
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

      const nurses24h = nurses.filter((n: any) => {
        if (!n.createdAt) return false
        return new Date(n.createdAt) >= yesterday
      }).length

      const nclexPassedTotal = nurses.filter((n: any) => n.nclexStatus === "Passed").length
      const nclexPassed30d = nurses.filter((n: any) => {
        if (n.nclexStatus !== "Passed") return false
        if (!n.createdAt) return false
        return new Date(n.createdAt) >= thirtyDaysAgo
      }).length

      setKpis({
        total_nurses: nurses.length,
        nurses_24h: nurses24h,
        nclex_passed_total: nclexPassedTotal,
        nclex_passed_30d: nclexPassed30d
      })
    } catch (error) {
      console.error("Failed to fetch KPIs:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchQuickStats = async () => {
    try {
      const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      
      const [pendingApps, activeInvites, expiringInvites] = await Promise.all([
        lumi.entities.Applications.list({
          filter: { status: "pending" }
        }),
        lumi.entities.Invites.list({
          filter: { status: "pending" }
        }),
        lumi.entities.Invites.list({
          filter: {
            status: "pending",
            expires_at: { $lte: sevenDaysFromNow.toISOString() }
          }
        })
      ])

      setQuickStats({
        pending_applications: pendingApps.total,
        active_invites: activeInvites.total,
        expiring_invites_7d: expiringInvites.total
      })
    } catch (error) {
      console.error("Failed to fetch quick stats:", error)
    } finally {
      setLoadingQuickStats(false)
    }
  }

  const fetchNewestUsers = async () => {
    try {
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
      const result = await lumi.entities.UserProfiles.list({
        filter: {
          createdAt: { $gte: yesterday.toISOString() }
        },
        sort: { _id: -1 },
        limit: 5
      })
      setNewestUsers(result.list)
    } catch (error) {
      console.error("Failed to fetch newest users:", error)
    } finally {
      setLoadingUsers(false)
    }
  }

  React.useEffect(() => {
    fetchKPIs()
    fetchQuickStats()
    fetchNewestUsers()
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchKPIs()
      fetchQuickStats()
      fetchNewestUsers()
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor(diffMs / (1000 * 60))
    
    if (diffHours < 1) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">System Overview</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Activity className="w-4 h-4 animate-pulse text-green-500" />
          <span>Live • Updates every 30s</span>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
          <p className="text-sm text-blue-600 mb-1 font-medium">Total Nurses</p>
          {loading ? (
            <div className="animate-pulse h-8 bg-blue-200 rounded w-20"></div>
          ) : (
            <p className="text-3xl font-bold text-blue-900">{kpis.total_nurses}</p>
          )}
        </div>
        <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border-2 border-teal-200">
          <p className="text-sm text-teal-600 mb-1 font-medium">Joined (24h)</p>
          {loading ? (
            <div className="animate-pulse h-8 bg-teal-200 rounded w-20"></div>
          ) : (
            <p className="text-3xl font-bold text-teal-900">{kpis.nurses_24h}</p>
          )}
        </div>
        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
          <p className="text-sm text-purple-600 mb-1 font-medium">NCLEX Passed (All-time)</p>
          {loading ? (
            <div className="animate-pulse h-8 bg-purple-200 rounded w-20"></div>
          ) : (
            <p className="text-3xl font-bold text-purple-900">{kpis.nclex_passed_total}</p>
          )}
        </div>
        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
          <p className="text-sm text-green-600 mb-1 font-medium">NCLEX Passed (30d)</p>
          {loading ? (
            <div className="animate-pulse h-8 bg-green-200 rounded w-20"></div>
          ) : (
            <p className="text-3xl font-bold text-green-900">{kpis.nclex_passed_30d}</p>
          )}
        </div>
      </div>

      {/* Quick Action Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 bg-white border-2 border-amber-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/superadmin/applications')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1 font-medium">Pending Applications</p>
              {loadingQuickStats ? (
                <div className="animate-pulse h-7 bg-gray-200 rounded w-16"></div>
              ) : (
                <p className="text-2xl font-bold text-amber-600">{quickStats.pending_applications}</p>
              )}
            </div>
            <CheckCircle className="w-8 h-8 text-amber-400" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Click to review →</p>
        </div>

        <div className="p-4 bg-white border-2 border-green-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/superadmin/invites')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1 font-medium">Active Invites</p>
              {loadingQuickStats ? (
                <div className="animate-pulse h-7 bg-gray-200 rounded w-16"></div>
              ) : (
                <p className="text-2xl font-bold text-green-600">{quickStats.active_invites}</p>
              )}
            </div>
            <Shield className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Manage invites →</p>
        </div>

        <div className="p-4 bg-white border-2 border-red-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/superadmin/invites')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1 font-medium">Expiring Soon (7d)</p>
              {loadingQuickStats ? (
                <div className="animate-pulse h-7 bg-gray-200 rounded w-16"></div>
              ) : (
                <p className="text-2xl font-bold text-red-600">{quickStats.expiring_invites_7d}</p>
              )}
            </div>
            <Clock className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Renew invites →</p>
        </div>
      </div>

      {/* Newest Users Widget */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Newest Users (Last 24h)</h3>
          <button
            onClick={() => navigate("/dashboard/superadmin/users?filter=24h&role=all")}
            className="text-sm text-amber-600 hover:text-amber-700 font-medium"
          >
            View All New Users →
          </button>
        </div>
        
        {loadingUsers ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="animate-pulse space-y-3">
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : newestUsers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No new users in the last 24 hours</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Country</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {newestUsers.map(user => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{user.fullName || user.email}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.country || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-right">
                      {user.createdAt ? formatTimeAgo(user.createdAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="space-y-4 mt-8">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="font-semibold text-amber-900 mb-2">Superadmin Access</h3>
          <p className="text-sm text-amber-700">
            You have full system access including user management, impersonation, invite generation, and audit logs.
          </p>
        </div>
      </div>
    </div>
  )
}



// Settings Tab
function SettingsTab() {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings</h2>
      <div className="text-center py-12 text-gray-500">
        <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p>System settings interface coming soon</p>
        <p className="text-sm mt-2">Configure platform settings, integrations, and features</p>
      </div>
    </div>
  )
}

// Applications Tab - Review pending access requests
function ApplicationsTab() {
  const [applications, setApplications] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    try {
      const result = await lumi.entities.Applications.list({
        filter: { status: "pending" }
      })
      setApplications(result.list)
    } catch (error) {
      console.error("Error fetching applications:", error)
      toast.error("Failed to load applications")
    } finally {
      setLoading(false)
    }
  }

  const handleApproveAndInvite = async (app: any) => {
    try {
      // Update application status
      await lumi.entities.Applications.update(app._id, { 
        status: "approved",
        reviewed_by: lumi.auth.user?.email || "superadmin",
        reviewed_at: new Date().toISOString()
      })

      // Generate invite token
      const expiresAt = new Date(Date.now() + (7 * 24 * 60 * 60 * 1000))
      const token = btoa(JSON.stringify({
        role: app.role_requested,
        email: app.email,
        region: app.region,
        expires: expiresAt.getTime()
      }))
      
      const inviteUrl = `${window.location.origin}/onboarding/role?invite=${app.role_requested}&token=${token}`

      // Store invite in database
      await lumi.entities.Invites.create({
        role: app.role_requested,
        email: app.email,
        name: app.name || app.contact_name,
        region: app.region,
        invite_url: inviteUrl,
        token: token,
        expires_at: expiresAt.toISOString(),
        status: "pending",
        created_by: lumi.auth.user?.email || "superadmin"
      })

      // Send email notification
      await sendInviteEmailDirect({
        role: app.role_requested,
        email: app.email,
        name: app.name || app.contact_name,
        region: app.region
      }, inviteUrl, expiresAt)

      toast.success(`Approved and invited ${app.name || app.contact_name}!`)
      fetchApplications()
    } catch (error) {
      console.error("Error approving application:", error)
      toast.error("Failed to approve and invite")
    }
  }

  const sendInviteEmailDirect = async (data: any, inviteUrl: string, expiresAt: Date) => {
    const roleLabels: Record<string, string> = {
      recruiter: "Recruiter",
      affiliate: "Affiliate",
      admin: "Admin",
      admin_regional: "Regional Admin"
    }

    const roleLabel = roleLabels[data.role] || data.role
    const adminEmail = lumi.auth.user?.email || "hello@nursinghive.com"

    try {
      await lumi.tools.email.send({
        to: adminEmail,
        subject: `[ACTION REQUIRED] Forward invite to ${data.email}`,
        fromName: "Nursing Hive Superadmin",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #0B253A;">Application Approved - Invite Generated</h2>
            
            <p><strong>Please forward this invite to:</strong> ${data.email}</p>
            
            <div style="background: #F4F7FA; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #0FB5AE; margin-top: 0;">Invitation Details</h3>
              <p><strong>Role:</strong> ${roleLabel}</p>
              ${data.name ? `<p><strong>Name:</strong> ${data.name}</p>` : ""}
              ${data.region ? `<p><strong>Region:</strong> ${data.region}</p>` : ""}
              <p><strong>Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
            </div>

            <div style="background: #0FB5AE; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: white; margin-top: 0;">Invite Link</h3>
              <p style="color: white; word-break: break-all;">
                <a href="${inviteUrl}" style="color: white; text-decoration: underline;">${inviteUrl}</a>
              </p>
            </div>

            <div style="border-left: 4px solid #0FB5AE; padding-left: 16px; margin: 20px 0;">
              <p style="color: #5F7483; margin: 0;"><strong>Template to forward:</strong></p>
              <p style="color: #5F7483; margin-top: 10px;">
                Hi${data.name ? ` ${data.name}` : ""},<br><br>
                Congratulations! Your application has been approved.<br><br>
                You've been invited to join <strong>Nursing Hive</strong> as <strong>${roleLabel}</strong>${data.region ? ` (Region: ${data.region})` : ""}.<br><br>
                Click the secure link below to accept your invitation:<br>
                <a href="${inviteUrl}">${inviteUrl}</a><br><br>
                This link expires on <strong>${expiresAt.toLocaleDateString()}</strong> and can be used only once.<br><br>
                — Nursing Hive Team
              </p>
            </div>

            <p style="color: #5F7483; font-size: 12px; margin-top: 30px;">
              This is an automated notification from Nursing Hive Superadmin dashboard.
            </p>
          </div>
        `,
        text: `
Application Approved - Invite Generated

Please forward this invite to: ${data.email}

Role: ${roleLabel}
${data.name ? `Name: ${data.name}\n` : ""}${data.region ? `Region: ${data.region}\n` : ""}Expires: ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}

Invite Link: ${inviteUrl}

--- Template to forward ---

Hi${data.name ? ` ${data.name}` : ""},

Congratulations! Your application has been approved.

You've been invited to join Nursing Hive as ${roleLabel}${data.region ? ` (Region: ${data.region})` : ""}.

Click the secure link below to accept your invitation:
${inviteUrl}

This link expires on ${expiresAt.toLocaleDateString()} and can be used only once.

— Nursing Hive Team
        `
      })
    } catch (error) {
      console.error("Error sending invite email:", error)
      toast("Invite created but email failed. Copy link manually.", { icon: "⚠️" })
    }
  }

  const handleReject = async (app: any) => {
    try {
      await lumi.entities.Applications.update(app._id, { status: "rejected" })
      toast.success("Application rejected")
      fetchApplications()
    } catch (error) {
      console.error("Error rejecting application:", error)
      toast.error("Failed to reject application")
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Pending Access Requests</h2>
      <p className="text-gray-600 mb-6">Review and approve recruiter and affiliate applications.</p>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p>No pending applications</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Applicant</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Region</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applications.map(app => (
                <tr key={app._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{app.name || app.contact_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{app.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                      {app.role_requested}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{app.region || "N/A"}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                      {app.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right space-x-2">
                    <button
                      onClick={() => handleApproveAndInvite(app)}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium"
                    >
                      Approve & Invite
                    </button>
                    <button
                      onClick={() => handleReject(app)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Old Invites Tab (now replaced by SuperadminInvites page)
// Kept temporarily for reference - can be deleted
function InvitesTab_OLD() {
  const [formData, setFormData] = React.useState({
    role: "recruiter",
    region: "",
    email: "",
    name: "",
    ttl_days: 7
  })
  const [inviteUrl, setInviteUrl] = React.useState("")
  const [sending, setSending] = React.useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    
    try {
      // Generate invite token (simplified - in production, store in DB with expiry)
      const expiresAt = new Date(Date.now() + (formData.ttl_days * 24 * 60 * 60 * 1000))
      const token = btoa(JSON.stringify({
        role: formData.role,
        email: formData.email,
        region: formData.region,
        expires: expiresAt.getTime()
      }))
      
      const url = `${window.location.origin}/onboarding/role?invite=${formData.role}&token=${token}`
      setInviteUrl(url)

      // Store invite in database for tracking
      await lumi.entities.Invites.create({
        role: formData.role,
        email: formData.email,
        name: formData.name,
        region: formData.region,
        invite_url: url,
        token: token,
        expires_at: expiresAt.toISOString(),
        status: "pending",
        created_by: lumi.auth.user?.email || "superadmin"
      })

      // Send email notification with invite template
      await sendInviteEmail(formData, url, expiresAt)

      toast.success("Invite link generated and email sent!")
    } catch (error) {
      console.error("Error generating invite:", error)
      toast.error("Failed to generate invite link")
    } finally {
      setSending(false)
    }
  }

  const sendInviteEmail = async (data: typeof formData, inviteUrl: string, expiresAt: Date) => {
    const roleLabels: Record<string, string> = {
      recruiter: "Recruiter",
      affiliate: "Affiliate",
      admin: "Admin",
      admin_regional: "Regional Admin"
    }

    const roleLabel = roleLabels[data.role] || data.role

    try {
      // Note: Lumi email system requires registered users
      // For unregistered invitees, we'll send to admin for manual forwarding
      const adminEmail = lumi.auth.user?.email || "hello@nursinghive.com"

      await lumi.tools.email.send({
        to: adminEmail,
        subject: `[ACTION REQUIRED] Forward invite to ${data.email}`,
        fromName: "Nursing Hive Superadmin",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #0B253A;">New Invite Generated</h2>
            
            <p><strong>Please forward this invite to:</strong> ${data.email}</p>
            
            <div style="background: #F4F7FA; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #0FB5AE; margin-top: 0;">Invitation Details</h3>
              <p><strong>Role:</strong> ${roleLabel}</p>
              ${data.name ? `<p><strong>Name:</strong> ${data.name}</p>` : ""}
              ${data.region ? `<p><strong>Region:</strong> ${data.region}</p>` : ""}
              <p><strong>Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
            </div>

            <div style="background: #0FB5AE; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: white; margin-top: 0;">Invite Link</h3>
              <p style="color: white; word-break: break-all;">
                <a href="${inviteUrl}" style="color: white; text-decoration: underline;">${inviteUrl}</a>
              </p>
            </div>

            <div style="border-left: 4px solid #0FB5AE; padding-left: 16px; margin: 20px 0;">
              <p style="color: #5F7483; margin: 0;"><strong>Template to forward:</strong></p>
              <p style="color: #5F7483; margin-top: 10px;">
                Hi${data.name ? ` ${data.name}` : ""},<br><br>
                You've been invited to join <strong>Nursing Hive</strong> as <strong>${roleLabel}</strong>${data.region ? ` (Region: ${data.region})` : ""}.<br><br>
                Click the secure link below to accept your invitation:<br>
                <a href="${inviteUrl}">${inviteUrl}</a><br><br>
                This link expires on <strong>${expiresAt.toLocaleDateString()}</strong> and can be used only once.<br><br>
                — Nursing Hive Team
              </p>
            </div>

            <p style="color: #5F7483; font-size: 12px; margin-top: 30px;">
              This is an automated notification from Nursing Hive Superadmin dashboard.
            </p>
          </div>
        `,
        text: `
New Invite Generated

Please forward this invite to: ${data.email}

Role: ${roleLabel}
${data.name ? `Name: ${data.name}\n` : ""}${data.region ? `Region: ${data.region}\n` : ""}Expires: ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}

Invite Link: ${inviteUrl}

--- Template to forward ---

Hi${data.name ? ` ${data.name}` : ""},

You've been invited to join Nursing Hive as ${roleLabel}${data.region ? ` (Region: ${data.region})` : ""}.

Click the secure link below to accept your invitation:
${inviteUrl}

This link expires on ${expiresAt.toLocaleDateString()} and can be used only once.

— Nursing Hive Team
        `
      })
    } catch (error) {
      console.error("Error sending invite email:", error)
      // Don't throw - invite URL is still generated
      toast("Invite created but email failed. Copy link manually.", { icon: "⚠️" })
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl)
    toast.success("Invite URL copied to clipboard!")
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Invite Link</h2>
      <p className="text-gray-600 mb-6">Create one-time invites for recruiters, affiliates, or admins.</p>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Email notifications are sent to your admin account for manual forwarding to the invitee. 
          This ensures security and allows you to add personal context before sending.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            required
          >
            <option value="recruiter">Recruiter</option>
            <option value="affiliate">Affiliate</option>
            <option value="admin">Admin</option>
            <option value="admin_regional">Admin – Regional</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Invitee Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="Full Name (optional)"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Invitee Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="user@example.com"
            required
          />
        </div>

        {(formData.role === "affiliate" || formData.role === "admin_regional") && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Region (if applicable)
            </label>
            <input
              type="text"
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="e.g., Pakistan, GCC"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Expires in (days)
          </label>
          <input
            type="number"
            value={formData.ttl_days}
            onChange={(e) => setFormData({ ...formData, ttl_days: parseInt(e.target.value) })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            min="1"
            max="30"
          />
        </div>

        <button
          type="submit"
          disabled={sending}
          className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? "Generating..." : "Generate Invite Link & Send Email"}
        </button>
      </form>

      {inviteUrl && (
        <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-900 mb-3">Invite URL Generated</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={inviteUrl}
              readOnly
              className="flex-1 px-4 py-2 bg-white border border-green-300 rounded text-sm"
            />
            <button
              onClick={copyToClipboard}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
            >
              Copy
            </button>
          </div>
          <p className="text-sm text-green-700 mt-3">
            Share privately; link expires after use or when TTL ends.
          </p>
        </div>
      )}
    </div>
  )
}

// Old InvitesTableTab (now replaced by SuperadminInvites page)
// Kept temporarily for reference - can be deleted
function InvitesTableTab_OLD() {
  const [invites, setInvites] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    fetchInvites()
  }, [])

  const fetchInvites = async () => {
    try {
      const result = await lumi.entities.Invites.list({
        sort: { _id: -1 }
      })
      setInvites(result.list)
    } catch (error) {
      console.error("Error fetching invites:", error)
      toast.error("Failed to load invites")
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async (invite: any) => {
    try {
      const expiresAt = new Date(invite.expires_at)
      await sendInviteEmail(
        {
          role: invite.role,
          email: invite.email,
          name: invite.name,
          region: invite.region,
          ttl_days: 0
        },
        invite.invite_url,
        expiresAt
      )
      toast.success(`Invite resent to ${invite.email}`)
    } catch (error) {
      console.error("Error resending invite:", error)
      toast.error("Failed to resend invite")
    }
  }

  const handleRevoke = async (invite: any) => {
    if (!confirm("This will invalidate the invite link. Continue?")) return
    
    try {
      await lumi.entities.Invites.update(invite._id, { status: "revoked" })
      toast.success("Invite revoked")
      fetchInvites()
    } catch (error) {
      console.error("Error revoking invite:", error)
      toast.error("Failed to revoke invite")
    }
  }

  const sendInviteEmail = async (data: any, inviteUrl: string, expiresAt: Date) => {
    const roleLabels: Record<string, string> = {
      recruiter: "Recruiter",
      affiliate: "Affiliate",
      admin: "Admin",
      admin_regional: "Regional Admin"
    }

    const roleLabel = roleLabels[data.role] || data.role
    const adminEmail = lumi.auth.user?.email || "hello@nursinghive.com"

    await lumi.tools.email.send({
      to: adminEmail,
      subject: `[ACTION REQUIRED] Forward invite to ${data.email}`,
      fromName: "Nursing Hive Superadmin",
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #0B253A;">New Invite Generated</h2>
          
          <p><strong>Please forward this invite to:</strong> ${data.email}</p>
          
          <div style="background: #F4F7FA; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #0FB5AE; margin-top: 0;">Invitation Details</h3>
            <p><strong>Role:</strong> ${roleLabel}</p>
            ${data.name ? `<p><strong>Name:</strong> ${data.name}</p>` : ""}
            ${data.region ? `<p><strong>Region:</strong> ${data.region}</p>` : ""}
            <p><strong>Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
          </div>

          <div style="background: #0FB5AE; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: white; margin-top: 0;">Invite Link</h3>
            <p style="color: white; word-break: break-all;">
              <a href="${inviteUrl}" style="color: white; text-decoration: underline;">${inviteUrl}</a>
            </p>
          </div>

          <div style="border-left: 4px solid #0FB5AE; padding-left: 16px; margin: 20px 0;">
            <p style="color: #5F7483; margin: 0;"><strong>Template to forward:</strong></p>
            <p style="color: #5F7483; margin-top: 10px;">
              Hi${data.name ? ` ${data.name}` : ""},<br><br>
              You've been invited to join <strong>Nursing Hive</strong> as <strong>${roleLabel}</strong>${data.region ? ` (Region: ${data.region})` : ""}.<br><br>
              Click the secure link below to accept your invitation:<br>
              <a href="${inviteUrl}">${inviteUrl}</a><br><br>
              This link expires on <strong>${expiresAt.toLocaleDateString()}</strong> and can be used only once.<br><br>
              — Nursing Hive Team
            </p>
          </div>

          <p style="color: #5F7483; font-size: 12px; margin-top: 30px;">
            This is an automated notification from Nursing Hive Superadmin dashboard.
          </p>
        </div>
      `,
      text: `
New Invite Generated

Please forward this invite to: ${data.email}

Role: ${roleLabel}
${data.name ? `Name: ${data.name}\n` : ""}${data.region ? `Region: ${data.region}\n` : ""}Expires: ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}

Invite Link: ${inviteUrl}

--- Template to forward ---

Hi${data.name ? ` ${data.name}` : ""},

You've been invited to join Nursing Hive as ${roleLabel}${data.region ? ` (Region: ${data.region})` : ""}.

Click the secure link below to accept your invitation:
${inviteUrl}

This link expires on ${expiresAt.toLocaleDateString()} and can be used only once.

— Nursing Hive Team
      `
    })
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A"
    return new Date(dateStr).toLocaleDateString()
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Issued Invites</h2>
      <p className="text-gray-600 mb-6">Manage, resend, or revoke invite links.</p>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Loading invites...</p>
        </div>
      ) : invites.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p>No invites issued yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Region</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Expires</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invites.map(invite => (
                <tr key={invite._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{invite.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      {invite.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{invite.region || "N/A"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(invite.expires_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      invite.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      invite.status === "used" ? "bg-green-100 text-green-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {invite.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right space-x-2">
                    {invite.status === "pending" && (
                      <>
                        <button
                          onClick={() => handleResend(invite)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                        >
                          Resend Email
                        </button>
                        <button
                          onClick={() => handleRevoke(invite)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}



// Audit Logs Tab
function AuditLogsTab() {
  const [logs, setLogs] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)
  const [filters, setFilters] = React.useState({
    actor_email: "",
    action: "",
    target_email: "",
    since_days: 30
  })

  React.useEffect(() => {
    fetchLogs()
  }, [])

  const fetchLogs = async () => {
    try {
      const sinceDaysAgo = new Date(Date.now() - (filters.since_days * 24 * 60 * 60 * 1000))
      
      const filter: any = {
        createdAt: { $gte: sinceDaysAgo.toISOString() }
      }

      if (filters.actor_email) filter.actor_email = { $regex: filters.actor_email, $options: "i" }
      if (filters.action) filter.action = filters.action
      if (filters.target_email) filter.target_email = { $regex: filters.target_email, $options: "i" }

      const result = await lumi.entities.AuditLogs.list({
        filter,
        sort: { _id: -1 },
        limit: 100
      })
      
      setLogs(result.list)
    } catch (error) {
      console.error("Error fetching audit logs:", error)
      toast.error("Failed to load audit logs")
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleApplyFilters = () => {
    setLoading(true)
    fetchLogs()
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Audit Logs</h2>
      <p className="text-gray-600 mb-6">Track all administrative actions and system events.</p>

      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="grid md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Actor Email"
            value={filters.actor_email}
            onChange={(e) => handleFilterChange("actor_email", e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange("action", e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="">All Actions</option>
            <option value="invite_created">Invite Created</option>
            <option value="invite_revoked">Invite Revoked</option>
            <option value="invite_used">Invite Used</option>
            <option value="application_approved">Application Approved</option>
            <option value="application_rejected">Application Rejected</option>
            <option value="user_impersonation">User Impersonation</option>
          </select>
          <input
            type="text"
            placeholder="Target Email"
            value={filters.target_email}
            onChange={(e) => handleFilterChange("target_email", e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
          <input
            type="number"
            placeholder="Since (days)"
            value={filters.since_days}
            onChange={(e) => handleFilterChange("since_days", parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            min="1"
            max="365"
          />
        </div>
        <button
          onClick={handleApplyFilters}
          className="mt-4 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold"
        >
          Apply Filters
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p>No audit logs found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">When</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actor</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Target</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map(log => (
                <tr key={log._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{log.actor_email || "—"}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{log.target_email || "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.metadata ? JSON.stringify(log.metadata).substring(0, 50) + "..." : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
